import { writable } from 'svelte/store';
import cloneDeep from 'lodash/cloneDeep';
import ElementNameRenderer from './ElementNameRenderer.svelte';
import SequenceColumnRenderer from './SequenceColumnRenderer.svelte';
import SequenceHeaderRenderer from './SequenceHeaderRenderer.svelte';

export const selectedListItems = writable([]);
export const isCopy = writable(true);
export const isDragging = writable(false);

export function updateLocalElementInstance(
  roles,
  roleId,
  worksequenceId,
  elementInstanceId,
  elementInstance
) {
  const role = roles.filter((r) => r.id === roleId);
  if (role.length) {
    const worksequence = role[0].worksequences.filter(
      (w) => w.id === worksequenceId
    );
    if (worksequence.length) {
      const filtered = worksequence[0].element_instances.filter(
        (e) => e.id !== elementInstanceId
      );

      filtered.push(elementInstance);
      filtered.sort((a, b) => a.step - b.step);
      worksequence[0].element_instances = filtered;
      updateWorkSequenceTime(worksequence[0]);
    }
  }

  return [...roles];
}

function updateWorkSequenceTime(ws) {
  ws.total_time = 0;
  ws.travel_time = 0;
  for (const e of ws.element_instances) {
    ws.total_time += e.total_time;
    ws.travel_time += e.travel_time;
  }
}

export function addElementToLocalWorksequence(
  roles,
  roleId,
  worksequenceId,
  elementInstance,
  selected
) {
  const worksequence = getWorkSequence(roles, roleId, worksequenceId);
  if (worksequence) {
    if (selected) {
      elementInstance.selected = true;
    }
    const last_element_instance =
      worksequence.element_instances[worksequence.element_instances.length - 1];

    elementInstance.step = last_element_instance
      ? last_element_instance.step + 1
      : 0;
    worksequence.element_instances.push(elementInstance);
  }

  return [...roles];
}

export function getLocalElementInstance(
  roles,
  roleId,
  worksequenceId,
  elementInstanceId
) {
  const role = roles.filter((r) => r.id === roleId);
  if (role.length) {
    const worksequence = role[0].worksequences.filter(
      (w) => w.id === worksequenceId
    );
    if (worksequence.length) {
      const filtered = worksequence[0].element_instances.filter(
        (e) => e.id === elementInstanceId
      );

      if (filtered.length) {
        return filtered[0];
      }
    }
  }

  return null;
}

export function getLocalWorkSequences(roles, items) {
  const result = [];

  let role = {};
  for (const i of items) {
    if (role.id != i.role_id) {
      role = roles.find((r) => r.id === i.role_id);
    }

    const ws = role.worksequences.find((s) => s.id === i.id);
    if (ws) {
      result.push(ws);
    }
  }

  return result;
}

export function getLocalElementInstances(
  roles,
  elements,
  targetRoleId,
  targetSeqId
) {
  const result = [];

  let role = {};
  let sequence = {};

  for (const e of elements) {
    if (!e) {
      continue;
    }
    if (role.id != e.role_id) {
      role = roles.find((r) => r.id === e.role_id);
    }
    if (sequence.id != e.work_sequence_id) {
      sequence = role.worksequences.find((s) => s.id === e.work_sequence_id);
    }

    let element = sequence.element_instances.find((el) => el.id === e.id);
    if (element === undefined && targetSeqId && targetRoleId) {
      //search in target sequence
      role = roles.find((r) => r.id === targetRoleId);
      sequence = role.worksequences.find((s) => s.id === targetSeqId);
      element = sequence.element_instances.find((el) => el.id === e.id);
    }
    result.push(element);
  }

  return result;
}

export function clearSequenceListSelection(roles) {
  roles.forEach((role) => {
    role.worksequences.forEach((ws) => {
      const selectedElements = ws.element_instances.filter((e) => e.selected);
      selectedElements.forEach((e) => delete e.selected);
    });
  });
  return [...roles];
}

export function addLocalNewDraftElementVersion(
  roles,
  roleId,
  sequenceId,
  eInstanceId,
  version
) {
  const role = roles.filter((r) => r.id === roleId);
  if (role.length) {
    const worksequence = role[0].worksequences.filter(
      (w) => w.id === sequenceId
    );
    if (worksequence.length) {
      const instance = worksequence[0].element_instances.find(
        (e) => e.id === eInstanceId
      );
      if (instance) {
        // update all element instances that have the same applicable_eev
        roles.forEach((r) => {
          r.worksequences.forEach((ws) => {
            const related_elements = ws.element_instances.filter(
              (e) => e.applicable_eev === instance.applicable_eev
            );
            related_elements.forEach(
              (ei) => (ei.versions = [version, ...ei.versions])
            );
          });
        });
      }
    }
  }

  return [...roles];
}

export function removeLocalElementInstance(
  roles,
  roleId,
  sequenceId,
  instanceIds
) {
  const role = roles.filter((r) => r.id === roleId);
  if (role.length) {
    const worksequence = role[0].worksequences.filter(
      (w) => w.id === sequenceId
    );
    if (worksequence.length) {
      const instances = worksequence[0].element_instances.filter(
        (ei) => !instanceIds.includes(ei.id)
      );
      worksequence[0].element_instances = instances;
    }
  }

  return [...roles];
}

export function removeLocalDraftElementVersion(
  roles,
  roleId,
  sequenceId,
  eInstanceId,
  eevId
) {
  const role = roles.filter((r) => r.id === roleId);
  if (role.length) {
    const worksequence = role[0].worksequences.filter(
      (w) => w.id === sequenceId
    );
    if (worksequence.length) {
      const instance = worksequence[0].element_instances.find(
        (e) => e.id === eInstanceId
      );
      if (instance) {
        instance.versions = instance.versions.filter((v) => v.id !== eevId);
        instance.status = 'Archived';
      }
    }
  }

  return [...roles];
}

export function getClosestNode(nodes, selector) {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].matches(selector)) {
      return nodes[i];
    }
  }
  return null;
}

export function reorderElementsWithinSequence(roles, from, to) {
  const role = roles.filter((r) => r.id === from.roleId);
  if (role.length) {
    const worksequence = role[0].worksequences.filter(
      (w) => w.id === from.wsId
    );
    if (worksequence.length) {
      let affected = [];
      if (from.step < to.step) {
        affected = worksequence[0].element_instances.filter(
          (e) => e.step >= from.step && e.step <= to.step
        );
        affected = [...affected.slice(1), affected[0]];
      } else {
        affected = worksequence[0].element_instances.filter(
          (e) => e.step >= to.step && e.step <= from.step
        );
        affected = [affected.pop(), ...affected];
      }
      affected.forEach(
        (item, index) => (item.step = Math.min(from.step, to.step) + index)
      );
      const affectedIdList = affected.map((e) => e.id);
      const notAffected = worksequence[0].element_instances.filter(
        (ei) => !affectedIdList.includes(ei.id)
      );
      worksequence[0].element_instances = [...notAffected, ...affected];
      worksequence[0].element_instances.sort((a, b) => a.step - b.step);
    }
  }
  return [...roles];
}

export function clearSelectedListItems(
  roles,
  exceptionList = { roles: [], workSequences: [], elementInstances: [] }
) {
  roles
    .filter((r) => !exceptionList.roles.includes(r.id))
    .forEach((role) => {
      delete role.selected;
      delete role.copied;
      delete role.cut;
      delete role.active;
      role.worksequences
        .filter((ws) => !exceptionList.workSequences.includes(ws.id))
        .forEach((seq) => {
          delete seq.selected;
          delete seq.copied;
          delete seq.cut;
          delete seq.active;
          seq.element_instances
            .filter((ei) => !exceptionList.elementInstances.includes(ei.id))
            .forEach((elem) => {
              delete elem.selected;
              delete elem.copied;
              delete elem.cut;
              delete elem.active;
            });
        });
    });
  return [...roles];
}

export function setSelectedElements(roles, elementInstances, activeElementId) {
  let ws = {};
  for (const element of elementInstances) {
    if (ws.id != element.work_sequence_id) {
      ws = getWorkSequence(roles, element.role_id, element.work_sequence_id);
    }

    const instance = ws.element_instances.find((e) => e.id === element.id);

    if (instance) {
      instance.selected = true;
      instance.active = instance.id === activeElementId;
    }
  }

  return [...roles];
}

// TODO: to be deprecated use setSelectedWorkSequence instead
export function setSelectedSequence(roles, roleId, seqId) {
  const ws = getWorkSequence(roles, roleId, seqId);
  if (ws) {
    ws.selected = true;
    ws.active = true;
  }

  return [...roles];
}

export function setSelectedWorkSequences(roles, workSequences, activeSeqId) {
  for (const ws of workSequences) {
    const sequence = getWorkSequence(roles, ws.role_id, ws.id);
    if (sequence) {
      sequence.selected = true;
      sequence.active = ws.id === activeSeqId;
    }
  }

  return [...roles];
}

export function setSelectedRole(roles, roleId) {
  const role = roles.find((r) => r.id === roleId);
  if (role) {
    role.selected = true;
    role.active = true;
  }

  return [...roles];
}

export function getWorkSequence(roles, roleId, seqId) {
  const role = roles.filter((r) => r.id === roleId);
  if (role.length) {
    const worksequence = role[0].worksequences.filter((w) => w.id === seqId);
    if (worksequence.length) {
      return worksequence[0];
    }
  }
  return null;
}

export function pasteLocalCopiedElements(roles, copiedElements, destination) {
  const ws = getWorkSequence(
    roles,
    destination.entity.role_id,
    destination.entity.work_sequence_id
  );

  if (ws) {
    const clonedElements = [];
    copiedElements.forEach((elm) => clonedElements.push(cloneDeep(elm)));
    const unaffectedElements = ws.element_instances.slice(
      0,
      destination.entity.step + 1
    );
    const affectedElements = ws.element_instances.slice(
      destination.entity.step + 1
    );
    const combinedAffectedElements = [...clonedElements, ...affectedElements];
    const position = destination.entity.step + 1;

    combinedAffectedElements.forEach(
      (item, index) => (item.step = position + index)
    );

    ws.element_instances = [...unaffectedElements, ...combinedAffectedElements];
  }

  return [...roles];
}

export function updateLocalWorksequence(roles, roleId, seqId, key, newValue) {
  if (seqId) {
    const ws = getWorkSequence(roles, roleId, seqId);
    if (ws) {
      ws[key] = newValue;
    }
  } else {
    //new work sequence added
    const role = roles.find((r) => r.id === roleId);
    if (role) {
      role.worksequences = [...role.worksequences, newValue];
    }
  }

  return [...roles];
}

export function updateLocalRoles(roles, updated) {
  for (const r of updated) {
    const role = roles.find((f) => f.id === r.id);
    if (role) {
      role.cycle_time = r.cycle_time;
      const expanded = role.worksequences
        .filter((ex) => ex.expanded)
        .map((w) => w.id);
      role.worksequences = [...r.worksequences];
      for (const ws of role.worksequences) {
        if (expanded.includes(ws.id)) {
          ws.expanded = true;
        }
      }
    } else {
      roles.push(r);
    }
  }

  return [...roles];
}

export function calculateTotalTime(roles) {
  roles.forEach((role) => {
    let total_time = 0;
    let travel_time = 0;

    role.worksequences.forEach((ws) => {
      total_time += ws.total_time;
      travel_time += ws.travel_time;
    });

    role.total_time = total_time;
    role.travel_time = travel_time;
    role.header = generateHeader(travel_time, total_time);
  });

  return [...roles];
}

export function calculateRoleTotalTime(roles) {
  const result = {};

  roles.forEach((role) => {
    let total_time = 0;
    let travel_time = 0;
    role.worksequences.forEach((ws) => {
      total_time += ws.total_time;
      travel_time += ws.travel_time;
    });

    result[role.id] = {
      total_time,
      travel_time,
    };
  });

  return result;
}

export function generateHeader(travel_time, total_time) {
  return {
    name: {
      label: '',
      columnRenderer: ElementNameRenderer,
      class: 'shrinkable',
    },
    travel_time: {
      label: travel_time,
      width: '80',
      class: 'right',
      headerRenderer: SequenceHeaderRenderer,
      columnRenderer: SequenceColumnRenderer,
      iconClass: 'fa5-walking',
    },
    total_time: {
      label: total_time,
      width: '80',
      class: 'right',
      headerRenderer: SequenceHeaderRenderer,
      columnRenderer: SequenceColumnRenderer,
      iconClass: 'fa5-clock',
    },
  };
}

export function reorderLocalRoles(roles, reordered) {
  let r;
  for (r of reordered) {
    const role = roles.find((e) => e.id === r.id);
    if (role) {
      role.step = r.step;
      delete role.cut;
      delete role.selected;
      delete role.active;
      delete role.copied;
    }
  }

  return [...roles.sort((a, b) => a.step - b.step)];
}

export function insertRole(roles, dragged, droppedId, isAfter) {
  const selectedRole = roles.find((r) => r.id === dragged[0].id);
  const draggedIndex = roles.findIndex((r) => r.id === selectedRole.id);
  roles.splice(draggedIndex, 1);
  const afterSplice = [...roles];
  const droppedIndex = roles.findIndex((r) => r.id === droppedId);
  let index = isAfter ? droppedIndex + 1 : droppedIndex;
  if (draggedIndex != index) {
    if (index < 0) {
      index = 0;
    }
    roles.splice(index, 0, selectedRole);
    return true;
  }
  return false;
}

function getRoleFromSelectedItems(items) {
  const roles = [];
  items.forEach((e) => {
    if (e && !roles.includes(e.role_id)) {
      roles.push(e.role_id);
    }
  });

  return roles;
}

export function insertSequences(
  roles,
  selectedItems,
  targetId,
  targetRoleId,
  isAfter
) {
  const targetRole = roles.find((r) => r.id === targetRoleId);
  const sequences = getLocalWorkSequences(roles, selectedItems);
  const sourceRoles = getRoleFromSelectedItems(sequences);
  const selectedIds = sequences.map((ws) => ws.id);

  if (targetId != targetRoleId) {
    const sortedSequences = [...sequences];
    sortedSequences.forEach((e, i) => {
      e.step = i;
    });

    selectedItems.forEach((e) => {
      e.role_id = targetRoleId;
    });

    if (
      targetRoleId &&
      (sourceRoles.length > 1 || !sourceRoles.includes(targetRoleId))
    ) {
      // cross roles movement
      const filteredSources = sourceRoles.filter((s) => s != targetRoleId);
      filteredSources.forEach((s) => {
        const role = roles.find((r) => r.id === s);
        if (role) {
          role.worksequences = role.worksequences.filter(
            (e) => !selectedIds.includes(e.id)
          );
        }
      });
      const targetRole = roles.find((r) => r.id === targetRoleId);
      targetRole.worksequences = targetRole.worksequences.filter(
        (e) => !selectedIds.includes(e.id)
      );
      const targetIndex = targetRole.worksequences.findIndex(
        (w) => w.id === targetId
      );
      const index = isAfter ? targetIndex + 1 : targetIndex;
      targetRole.worksequences.splice(
        index < 0 ? 0 : index,
        0,
        ...sortedSequences
      );
      targetRole.worksequences.forEach((e, i) => {
        e.step = i;
      });
    } else {
      // same role movement
      const seqIds = sequences.map((w) => w.id);
      const staticSequences = targetRole.worksequences.filter(
        (w) => !seqIds.includes(w.id)
      );
      const targetIndex = staticSequences.findIndex((w) => w.id === targetId);
      const index = isAfter ? targetIndex + 1 : targetIndex;
      staticSequences.splice(index < 0 ? 0 : index, 0, ...sequences);
      staticSequences.forEach((e, i) => {
        e.step = i;
      });
      targetRole.worksequences = [...staticSequences];
    }
  }
}

function getWorkSequencesFromElements(elements) {
  const sequences = [];

  elements.forEach((e) => {
    if (e && !sequences.includes(e.work_sequence_id)) {
      sequences.push({
        roleId: e.role_id,
        sequenceId: e.work_sequence_id,
      });
    }
  });
  return sequences;
}

export function formatSelectedElements(
  roles,
  items,
  properties,
  targetRoleId,
  targetSeqId
) {
  const elements = getLocalElementInstances(
    roles,
    items,
    targetRoleId,
    targetSeqId
  );

  elements.forEach((e) => {
    if (e) {
      Object.keys(properties).forEach((k) => {
        e[k] = properties[k];
      });
    }
  });

  return elements;
}

export function formatSelectedSequences(roles, items, properties) {
  const sequences = getLocalWorkSequences(roles, items);
  sequences.forEach((e) => {
    Object.keys(properties).forEach((k) => {
      e[k] = properties[k];
    });
  });

  return sequences;
}

export function formatSelectedRoles(roles, items, properties) {
  const selectedItemsId = items.map((i) => i.id);
  const selected = roles.filter((r) => selectedItemsId.includes(r.id));
  selected.forEach((e) => {
    Object.keys(properties).forEach((k) => {
      e[k] = properties[k];
    });
  });

  return selected;
}

export function insertElements(
  roles,
  selectedItems,
  targetId,
  targetSeqId,
  targetRoleId,
  isAfter
) {
  if (targetRoleId && targetSeqId) {
    const targetRole = roles.find((r) => r.id === targetRoleId);
    const elements = getLocalElementInstances(
      roles,
      selectedItems,
      targetRoleId,
      targetSeqId
    );
    const sourceRoles = getRoleFromSelectedItems(elements);
    const sourceSequences = getWorkSequencesFromElements(elements);
    const selectedIds = elements.map((w) => w && w.id);

    //set the step according to order of selection
    const sortedElements = [...elements];
    sortedElements.forEach((e, i) => {
      if (e) {
        e.step = i;
        e.work_sequence_id = targetSeqId;
        e.role_id = targetRoleId;
      }
    });

    selectedItems.forEach((e) => {
      e.work_sequence_id = targetSeqId;
      e.role_id = targetRoleId;
    });

    if (
      targetSeqId &&
      (sourceSequences.length > 1 ||
        !sourceSequences.find((s) => s.sequenceId === targetSeqId))
    ) {
      //cross sequences movement
      const filteredSources = sourceSequences.filter(
        (s) => s.sequenceId != targetSeqId
      );
      filteredSources.forEach((s) => {
        const ws = getWorkSequence(roles, s.roleId, s.sequenceId);
        if (ws) {
          ws.element_instances = ws.element_instances.filter(
            (e) => !selectedIds.includes(e.id)
          );
        }
      });
      const targetSequence = getWorkSequence(roles, targetRoleId, targetSeqId);
      targetSequence.element_instances =
        targetSequence.element_instances.filter(
          (e) => !selectedIds.includes(e.id)
        );
      const targetIndex = targetSequence.element_instances.findIndex(
        (w) => w.id === targetId
      );
      const index = isAfter ? targetIndex + 1 : targetIndex;
      targetSequence.element_instances.splice(
        index < 0 ? 0 : index,
        0,
        ...sortedElements
      );
      targetSequence.element_instances.forEach((e, i) => {
        if (e) {
          e.step = i;
        }
      });
      targetSequence.element_instances = [...targetSequence.element_instances];
    } else {
      //same sequence movement
      const targetWs = targetRole.worksequences.find(
        (w) => w.id === targetSeqId
      );
      const staticItems = targetWs.element_instances.filter(
        (e) => !selectedIds.includes(e.id)
      );
      const targetIndex = staticItems.findIndex((w) => w.id === targetId);
      const index = isAfter ? targetIndex + 1 : targetIndex;

      staticItems.splice(index < 0 ? 0 : index, 0, ...sortedElements);
      staticItems.forEach((e, i) => {
        if (e) {
          e.step = i;
        }
      });
      targetWs.element_instances = [...staticItems];
    }
  }
}

export function removeRoleFromList(roles, role) {
  return roles.filter((r) => r.id === role.id);
}

export function saveToLocalStorage(key, data) {
  if (window.localStorage) {
    localStorage.setItem(key, JSON.stringify(data));
  }
}

export function getFromLocalStorage(key) {
  if (window.localStorage) {
    return JSON.parse(localStorage.getItem(key));
  }
  return null;
}

export function removeFromLocalStorage(key) {
  if (window.localStorage) {
    localStorage.removeItem(key);
  }
}

export function getListItemType(item) {
  let type;

  if (item.hasOwnProperty('worksequences')) {
    type = 'role';
  } else if (item.hasOwnProperty('element_instances')) {
    type = 'sequence';
  } else if (item.hasOwnProperty('element_id')) {
    type = 'element';
  }

  return type;
}

export function animateToPosition(
  draggedElement,
  destination,
  callback,
  duration
) {
  if (destination) {
    const destinationRect = destination.getBoundingClientRect();
    const newTransform = {
      x: destinationRect.left - parseFloat(draggedElement.style.left),
      y: destinationRect.top - parseFloat(draggedElement.style.top),
    };
    const transition = `transform ${duration}ms ease`;

    draggedElement.style.transition = draggedElement.transition
      ? draggedElement.style.transition + ',' + transition
      : transition;
    draggedElement.style.transform = `translate3d(${newTransform.x}px, ${newTransform.y}px, 0)`;

    window.setTimeout(callback, duration);
  }
}
